import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { adminAPI } from '../http/AdminApiClient';

type AuthState = 'checking' | 'authenticated' | 'anonymous';

type SessionResponse = {
  authenticated: boolean;
  csrfToken?: string | null;
};

type AuthContextValue = {
  state: AuthState;
  login(token: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthState>('checking');

  const applySession = useCallback((session: SessionResponse) => {
    adminAPI.setCSRFToken(session.csrfToken);
    setState(session.authenticated ? 'authenticated' : 'anonymous');
  }, []);

  const refresh = useCallback(async () => {
    try {
      applySession(await adminAPI.get<SessionResponse>('/admin/session'));
    } catch {
      adminAPI.setCSRFToken(null);
      setState('anonymous');
    }
  }, [applySession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (token: string) => {
    applySession(await adminAPI.post<SessionResponse>('/admin/session', { token }));
  }, [applySession]);

  const logout = useCallback(async () => {
    await adminAPI.delete<SessionResponse>('/admin/session');
    adminAPI.setCSRFToken(null);
    setState('anonymous');
  }, []);

  const value = useMemo(
    () => ({ state, login, logout, refresh }),
    [state, login, logout, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
